import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronLeftIcon } from './icons';

/** Condensed uppercase line above a title ("Gravel Games 2027 · 12–13 juin · Vercors"). */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="font-display text-helper font-bold uppercase tracking-[0.12em] text-ink-muted">
      {children}
    </div>
  );
}

/** Top of a non-checkout screen: eyebrow, big title, intro (screens 01, 05, 07). */
export function PageHeader({
  eyebrow,
  title,
  intro,
  aside,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  intro?: ReactNode;
  /** Right side of the eyebrow row (e.g. Déconnexion on the profile). */
  aside?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-1">
      {(eyebrow || aside) && (
        <div className="flex items-center justify-between gap-3">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {aside}
        </div>
      )}
      <h1 className="m-0 font-display text-title font-bold tracking-[-0.01em]">{title}</h1>
      {intro && <p className="mt-1 text-[15px] leading-snug text-ink-muted">{intro}</p>}
    </header>
  );
}

/** Round back link (checkout steps, invite from the profile 04b). */
export function BackButton({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      aria-label={label}
      className="flex size-10 shrink-0 items-center justify-center rounded-full border border-line bg-paper text-ink hover:text-ink"
    >
      <ChevronLeftIcon />
    </Link>
  );
}

/**
 * Checkout step header: round back button, progress segments, title (screens 02, 03).
 * No app navigation during checkout (PRD §5).
 */
export function StepHeader({
  backTo,
  backLabel,
  step,
  steps = 3,
  title,
}: {
  backTo: string;
  /** Accessible name of the back button, from i18n. */
  backLabel: string;
  /** 1-based index of the current step. */
  step: number;
  steps?: number;
  title: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-2.5">
      <div className="flex items-center gap-3">
        <BackButton to={backTo} label={backLabel} />
        <SegmentedProgress total={steps} done={step} label={`${step} / ${steps}`} thin />
      </div>
      <h1 className="m-0 mt-1.5 font-display text-title-step font-bold">{title}</h1>
    </header>
  );
}

/**
 * Bottom bar holding the screen's one primary action, with an optional total above it
 * (screens 01–04, 07). Sticks to the bottom of the viewport on phones.
 */
export function StickyFooter({
  totalLabel,
  total,
  note,
  children,
}: {
  totalLabel?: ReactNode;
  total?: ReactNode;
  /** Fine print under the action (Turnstile / Stripe mention). */
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="sticky bottom-0 -mx-5 mt-auto flex flex-col gap-2.5 border-t border-line bg-paper px-5 pt-3.5 pb-[max(28px,env(safe-area-inset-bottom))]">
      {total !== undefined && (
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-ink-muted">{totalLabel}</span>
          <span className="font-display text-total font-bold">{total}</span>
        </div>
      )}
      {children}
      {note && (
        <span className="flex items-center justify-center gap-1.5 text-center text-xs text-ink-subtle">
          {note}
        </span>
      )}
    </div>
  );
}

/** Continuous bar (profile completion). `value` from 0 to 1. */
export function ProgressBar({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className="h-2 overflow-hidden rounded-[4px] bg-line"
    >
      <div className="h-full rounded-[4px] bg-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Segments (checkout steps: thin `ink`; referral 2 / 5: thick `accent`). */
export function SegmentedProgress({
  total,
  done,
  label,
  thin,
}: {
  total: number;
  done: number;
  label: string;
  thin?: boolean;
}) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={Math.min(done, total)}
      className="flex flex-1 gap-1.5"
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`flex-1 ${thin ? 'h-1 rounded-[2px]' : 'h-2 rounded-[4px]'} ${
            i < done ? (thin ? 'bg-ink' : 'bg-accent') : 'bg-line'
          }`}
        />
      ))}
    </div>
  );
}
